import React from "react";

const tracksData = [
  {
    id: 1,
    number: "Track 1",
    trackTitle: "Intelligent Computing Architectures",
    tagline: 'The "Hard" Science',
    items: [
      {
        label: "Sustainable AI Infrastructure",
        content: "Green AI and carbon-aware computing.",
      },
      {
        label: "Edge Intelligence",
        content: "Resource-constrained AI for rural sustainability.",
      },
      {
        label: "Privacy-Preserving Learning",
        content: "Federated learning and differential privacy.",
      },
      {
        label: "Green Computing",
        content: "Algorithms designed for minimal carbon footprint.",
      },
    ],
  },
  {
    id: 2,
    number: "Track 2",
    trackTitle: "Cyber-Physical Systems (CPS) & Smart Infrastructure",
    items: [
      {
        label: "High-Assurance CPS",
        content: "Reliability in self-healing systems.",
      },
      {
        label: "Smart Grids",
        content: "Interoperability and demand-response systems.",
      },
      {
        label: "Digital Twins",
        content: "Modeling for urban infrastructure and climate resilience.",
      },
      {
        label: "IoT for Development",
        content: "Sensor networks for agriculture and water management.",
      },
    ],
  },
  {
    id: 3,
    number: "Track 3",
    trackTitle: "Security, Sovereignty & Governance",
    items: [
      {
        label: "Decentralized Governance",
        content: "Autonomous frameworks and identifiers (DIDs).",
      },
      {
        label: "Data Sovereignty",
        content: "Cryptographic methods for vulnerable populations.",
      },
      {
        label: "Algorithmic Fairness",
        content: "Auditing bias in automated decision-making.",
      },
      {
        label: "Blockchain for Sustainability",
        content: "Transparent supply chains and circular economies.",
      },
    ],
  },
  {
    id: 4,
    number: "Track 4",
    trackTitle: "Socio-Technical Dynamics & Digital Equity",
    items: [
      {
        label: "Human-Centric Computing",
        content: "Design for non-literate and elderly populations.",
      },
      {
        label: "Digital Inclusion",
        content: "Low-cost connectivity and localized content.",
      },
      {
        label: "Techno-Ethical Frameworks",
        content: "Integrating engineering with international law.",
      },
      {
        label: "Algorithmic Transparency",
        content: "Mitigating historical biases in social datasets.",
      },
    ],
  },
];

const Tracks = () => {
  return (
    <section
      id="conference-tracks"
      className="relative py-24 overflow-hidden bg-white"
    >
      {/* Decorative Background Elements */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-pink/5 rounded-full blur-[150px] -z-0"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-20 space-y-6">
          <span className="text-pink font-bold tracking-[0.3em] uppercase text-sm inline-block px-6 py-2 border border-pink/30 rounded-full bg-pink/5 animate-pulse">
            Research Scope
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-4xl font-extrabold text-slate-900 leading-tight tracking-tight uppercase">
            Key <span className="text-gradient">Tracks</span>
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-pink to-purple-600 mx-auto rounded-full"></div>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Exploring the frontiers of intelligence, security, and
            socio-technical ecosystems for a sustainable future.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {tracksData.map((track) => (
            <div
              key={track.id}
              className="premium-card bg-white p-12 rounded-[2.5rem] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] group transition-all duration-500 flex flex-col h-full relative overflow-hidden"
            >
              {/* Refined Track Number Backdrop */}
              <div className="absolute -top-2 -right-2 text-[90px] font-black text-slate-50 leading-none pointer-events-none group-hover:text-pink/5 transition-colors">
                {track.id}
              </div>

              <div className="mb-10 relative z-10">
                <span className="text-pink font-bold text-[10px] uppercase tracking-[0.25em] block mb-3">
                  {track.number}
                </span>
                <h3 className="text-xl md:text-2xl font-extrabold text-slate-900 leading-tight tracking-tight">
                  {track.trackTitle}
                  {track.tagline && (
                    <span className="block text-lg font-medium text-slate-400 mt-2 italic">
                      ({track.tagline})
                    </span>
                  )}
                </h3>
              </div>

              <ul className="space-y-8 flex-grow relative z-10">
                {track.items.map((item, idx) => (
                  <li key={idx} className="relative pl-10 group/item">
                    {/* Minimalist Indicator */}
                    <div className="absolute left-0 top-2.5 w-1.5 h-1.5 rounded-full bg-slate-200 group-hover/item:bg-pink transition-colors duration-300"></div>

                    <span className="block font-bold text-pink/80 mb-1 uppercase tracking-[0.2em] text-[10px]">
                      {item.label}
                    </span>
                    <p className="text-slate-600 font-medium leading-relaxed block text-base group-hover/item:text-slate-900 transition-colors">
                      {item.content}
                    </p>
                  </li>
                ))}
              </ul>

              {/* Decorative Corner Glow */}
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-pink/5 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          ))}
        </div>

        {/* Global Call to Action or Note */}
        <div className="mt-20 text-center">
          <div className="inline-block p-1 rounded-full bg-slate-50 border border-slate-100">
            <p className="px-8 py-3 text-slate-600 font-medium italic">
              "Fostering interdisciplinary dialogue across computing, ethics,
              and infrastructure."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tracks;
